import type { ValidationError } from '@/lib/graphql/generated/graphql';
import { filterBrandVisibleValidationErrors } from '@/lib/utils/brand-validation-visibility';
import { isInternalUserAtom } from '@/states/is-internal-user.state';
import {
  backendValidatedProductIdsAtom,
  localValidationErrorsAtom,
  productsMapState,
} from '@/states/products.state';
import { RulesManager } from '@appchoose/deal-validation-core';
import { atom } from 'jotai';

/**
 * Rule ids the local validation worker is able to recompute — i.e. exactly the rules
 * shipped in the shared core `RulesManager` (see `lib/workers/validation.worker.ts`).
 * Derived from the manager itself so it can never drift from what the worker actually runs.
 *
 * Backend errors whose rule id is NOT in this set (gtin validity, duplicates,
 * url_or_description_images_or_drive_link) come from domain-only rules the worker cannot evaluate. We preserve
 * their last-known backend value during optimistic local validation instead of dropping
 * them, otherwise they would flicker out on every edit and reappear once the backend
 * re-confirms.
 */
const WORKER_VALIDATED_RULE_IDS: ReadonlySet<string> = new Set(
  new RulesManager().getAllRules().map((rule) => rule.id)
);

export const effectiveValidationErrorsAtom = atom<
  Map<string, ValidationError[] | null>
>((get) => {
  const productsMap = get(productsMapState);
  const localErrors = get(localValidationErrorsAtom);
  const backendConfirmed = get(backendValidatedProductIdsAtom);
  const isInternalUser = get(isInternalUserAtom);
  const result = new Map<string, ValidationError[] | null>();

  for (const [id, product] of productsMap) {
    if (backendConfirmed.has(id)) {
      result.set(
        id,
        filterBrandVisibleValidationErrors(
          product.activeVersion?.validationErrors ?? [],
          isInternalUser
        )
      );
    } else if (localErrors !== null) {
      const localCoreErrors = localErrors.get(id) ?? [];
      const preservedBackendErrors = (
        product.activeVersion?.validationErrors ?? []
      ).filter((error) => !WORKER_VALIDATED_RULE_IDS.has(error.ruleId));
      result.set(
        id,
        filterBrandVisibleValidationErrors(
          [...localCoreErrors, ...preservedBackendErrors],
          isInternalUser
        )
      );
    } else {
      result.set(id, null);
    }
  }

  return result;
});
